import Drawer from '@material-ui/core/Drawer';
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';

import {HelpWebhookAutomation} from '../../../Webhooks/HelpWebhookAutomation';
import {ResizableDrawer} from '../../../Webhooks/ResizableDrawer';
import {getRunMetricChangeConfig} from '../CreateTriggerDrawer/RunMetricChangeSection';
import {getRunMetricThresholdConfig} from '../CreateTriggerDrawer/RunMetricThresholdSection';
import {getRunStateConfig} from '../CreateTriggerDrawer/RunStateSection';
import {getNotificationConfig} from '../notificationUtils';
import {getDefaultTriggerDescription} from '../utils';
import {useTriggerArtifactCollectionName} from './hooks';
import {ReadOnlyActionConfig} from './ReadOnlyActionConfig';
import {
  getActionInfo,
  getJobActionDetails,
  getWebhookActionDetails,
} from './utilData';
import {Trigger} from './utilTypes';
import {ViewTriggerEventActionSummary} from './ViewTriggerEventActionSummary';

type ViewTriggerDrawerProps = {
  open: boolean;
  closeDrawer: () => void;

  trigger: Trigger;
};

export const ViewTriggerDrawer = ({
  open,
  closeDrawer,
  trigger,
}: ViewTriggerDrawerProps) => {
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();

  const {actionTypeName, actionName, actionConfigString, configHeader} =
    getActionInfo(trigger);
  const {jobName} = getJobActionDetails(trigger);
  const {webhook} = getWebhookActionDetails(trigger);
  const date = new Date(trigger.createdAt);
  const createdDate = date.toDateString();
  const {
    data: artifactCollectionName,
    loading,
    error,
  } = useTriggerArtifactCollectionName(trigger.triggerScope);
  if (loading) {
    return <></>;
  }
  if (error) {
    console.error(
      'Could not query artifact collection name for default description value.'
    );
    return <></>;
  }

  const notifConfig = getNotificationConfig(trigger);

  const descriptionDict = getDefaultTriggerDescription(
    {
      eventType: trigger.triggeringCondition.eventType,
      triggerScopeType: trigger.triggerScope.scope,
      entityName,
      projectName,
      artifactCollectionName,
      filters: trigger.triggeringCondition.filter,
      runMetricConfig: getRunMetricThresholdConfig(trigger),
      runMetricChangeConfig: getRunMetricChangeConfig(trigger),
      runStateConfig: getRunStateConfig(trigger),
      notificationConfig: notifConfig,
    },
    trigger.triggeredAction?.actionType,
    jobName.name,
    jobName.alias,
    webhook.name,
    notifConfig.teamSlackIntegration?.channelName
  );

  return (
    <Drawer anchor="right" open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={500}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="h-full w-full px-24 pb-20 pt-80">
            <div className="flex h-full w-full flex-col">
              <div className="mb-20 flex items-center">
                <div className="text-xl font-semibold leading-8">
                  {trigger.name}
                </div>
                <div className="flex-1 text-right">
                  <Button
                    onClick={closeDrawer}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>
              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="w-full flex-1 overflow-auto px-8 py-32">
                <div className="mb-4 flex w-full justify-between">
                  <div className="w-1/3 text-left text-base leading-6 text-moon-550">
                    Name
                  </div>
                  <div className="text-gray-800 w-2/3 whitespace-normal break-all text-left text-base leading-6">
                    {trigger.name}
                  </div>
                </div>
                <div className="mb-4 mt-16 flex w-full justify-between">
                  <div className="w-1/3 text-left text-base leading-6 text-moon-550">
                    Description
                  </div>
                  <div className="text-gray-800 w-2/3 whitespace-normal text-left text-base leading-6">
                    {trigger.description}
                  </div>
                </div>
                <div className="mb-4 mt-16 flex w-full justify-between">
                  <div className="w-1/3 text-left text-base leading-6 text-moon-550">
                    Action type
                  </div>
                  <div className="text-gray-800 w-2/3 whitespace-normal break-all text-left text-base leading-6">
                    {actionTypeName}
                  </div>
                </div>
                <div className="mb-4 mt-16 flex w-full justify-between">
                  <div className="w-1/3 text-left text-base leading-6 text-moon-550">
                    Action name
                  </div>
                  <div className="text-gray-800 w-2/3 whitespace-normal break-all text-left text-base leading-6">
                    {actionName}
                  </div>
                </div>
                <div className="mb-4 mt-16 flex w-full justify-between">
                  <div className="w-1/3 text-left text-base leading-6 text-moon-550">
                    Date created
                  </div>
                  <div className="text-gray-800 w-2/3 whitespace-normal break-all text-left text-base leading-6">
                    {createdDate}
                  </div>
                </div>
                <div className="mt-32 w-full text-left text-base font-semibold leading-8">
                  Summary
                </div>
                <ViewTriggerEventActionSummary
                  descriptionDict={descriptionDict}
                />
                {configHeader && (
                  <>
                    <div className="mt-32 w-full text-left text-base font-semibold leading-8">
                      {configHeader}
                    </div>
                    <ReadOnlyActionConfig
                      actionConfig={actionConfigString}
                      actionTypeName={actionTypeName}
                    />
                  </>
                )}
                {actionTypeName === 'Webhooks' && (
                  <HelpWebhookAutomation
                    webhook={webhook}
                    requestPayload={actionConfigString}
                  />
                )}
              </div>
              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="flex items-center pt-20">
                <Button
                  variant="primary"
                  size="large"
                  className="w-full"
                  onClick={closeDrawer}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};
